import React, { useState, useEffect, useRef } from "react";
import Typed from "typed.js";
import profilepic from "../../assets/profilepic.jpeg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin, faGithub } from "@fortawesome/free-brands-svg-icons";
import {
  faCode,
  faDesktop,
  faMobile,
  faRocket,
  faDownload,
  faDatabase,
  faCloud,
  faCogs,
} from "@fortawesome/free-solid-svg-icons";
import "./Header.css";

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const typedRef = useRef(null);
  const codeRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (typedRef.current) {
      const typed = new Typed(typedRef.current, {
        strings: [
          "Building Scalable MERN Stack Applications 🚀",
          "Architecting Advanced Matchmaking Systems ✨",
          "Optimizing Performance by 40% with Smart Caching 📱",
          "Leading Full Product Lifecycle Development 💡",
          "Deploying Enterprise Solutions on AWS & Cloud ⚡",
        ],
        typeSpeed: 40,
        backSpeed: 25,
        backDelay: 2000,
        loop: true,
        fadeOut: false,
        fadeOutClass: '',
        fadeOutDelay: 0,
        smartBackspace: true,
      });

      return () => typed.destroy();
    }
  }, []);

  // Animated code snippets for badge
  useEffect(() => {
    if (codeRef.current) {
      const codeSnippets = [
        "const magic = () => 'Hello World!' 🎯",
        "[...array].reverse() // Clean code ✨",
        "async/await > promises 🚀",
        "const {name, age} = person 💫",
        "arr.map(x => x * 2) // Functional 🔄",
        "React.useState(0) // Hooks! ⚡",
        "API?.data?.user || 'fallback' 🛡️",
        "setTimeout(() => magic(), 1000) ⏱️"
      ];

      let currentIndex = 0;
      
      const animateCode = () => {
        const element = codeRef.current;
        if (!element) return;
        
        // Fade out
        element.style.opacity = '0';
        element.style.transform = 'translateY(10px)';
        
        setTimeout(() => {
          // Change text
          element.textContent = codeSnippets[currentIndex];
          currentIndex = (currentIndex + 1) % codeSnippets.length;
          
          // Fade in
          element.style.opacity = '1';
          element.style.transform = 'translateY(0)';
        }, 300);
      };

      // Initial display
      codeRef.current.textContent = codeSnippets[0];
      currentIndex = 1;
      
      // Start animation
      const interval = setInterval(animateCode, 3000);
      
      return () => clearInterval(interval);
    }
  }, []);

  const skills = [
    {
      icon: faCode,
      name: "MERN Stack Development",
      level: 95,
      desc: "React, Node.js, Express, MongoDB",
    },
    {
      icon: faDesktop,
      name: "Enterprise Web Applications",
      level: 92,
      desc: "Vue.js, Ruby on Rails, REST APIs",
    },
    {
      icon: faMobile,
      name: "React Native & Mobile",
      level: 88,
      desc: "Cross-platform iOS/Android apps",
    },
    {
      icon: faRocket,
      name: "Performance Optimization",
      level: 90,
      desc: "40% throughput increase, 30% latency reduction",
    },
    {
      icon: faDatabase,
      name: "Database Architecture",
      level: 89,
      desc: "MongoDB, PostgreSQL, Redis caching",
    },
    {
      icon: faCloud,
      name: "Cloud & DevOps",
      level: 87,
      desc: "AWS, CI/CD pipelines, automated deployment",
    },
    {
      icon: faCogs,
      name: "System Design",
      level: 91,
      desc: "Scalable architecture, microservices",
    },
  ];

  const stats = [
    { 
      number: "5+", 
      label: "Years Experience", 
      icon: faCode,
      tooltip: "5+ years of enterprise software development across fintech, government systems, and startup environments"
    },
    { 
      number: "30%", 
      label: "API Performance Boost", 
      icon: faRocket,
      tooltip: "Reduced API response latency by 30% through strategic caching, query optimization, and database indexing"
    },
    { 
      number: "40%", 
      label: "System Throughput Increase", 
      icon: faCogs,
      tooltip: "Increased system throughput by 40% using microservices architecture and load balancing optimizations"
    },
    { 
      number: "25%", 
      label: "Load Time Reduction", 
      icon: faDesktop,
      tooltip: "Achieved 25% faster load times through code splitting, lazy loading, and CDN implementation"
    },
  ];

  return (
    <div className="homepage-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div
          className="hero-background"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        >
          <div className="gradient-overlay"></div>
          <div className="floating-elements">
            <div className="floating-element element-1"></div>
            <div className="floating-element element-2"></div>
            <div className="floating-element element-3"></div>
            <div className="floating-element element-4"></div>
          </div>
        </div>

        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">
              <span ref={codeRef} className="badge-code"></span>
              <div className="badge-glow"></div>
            </div>

            <h1 className="hero-title">
              Hi, I'm <span className="highlight-name">Dawit Endaylalu</span>
            </h1>
            <p className="hero-description">
              Software Engineer with 5+ years developing enterprise solutions
              across fintech, data processing, and mobile applications. General
              Assembly graduate with proven expertise in full-stack development,
              system architecture, and delivering scalable solutions for complex
              business challenges.
            </p>
            

            <div className="hero-buttons">
              <a href="projects-dev" className="btn btn-primary">
                <FontAwesomeIcon icon={faRocket} />
                <span>View My Work</span>
              </a>
              <a
                href="https://www.linkedin.com/in/dawit-endaylalu/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                <FontAwesomeIcon icon={faLinkedin} />
                <span>Let's Connect</span>
              </a>
            </div>
            <div className="typed-container">
              <span ref={typedRef} className="typed-text"></span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-icon">
                <FontAwesomeIcon icon={stat.icon} />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
              <div className="stat-tooltip">
                {stat.tooltip}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="section-container">
          <div className="about-content">
            <div className="about-text">
              <div className="section-badge">
                <span>About Me</span>
              </div>

              <h2 className="section-title">
                Crafting Digital Solutions with{" "}
                <span className="highlight-text">Passion</span>
              </h2>

              <p className="about-description">
                Software Engineer with 6 years of expertise in web and mobile
                application development, backed by a Bachelor's degree in Food
                Science and Post-Harvest Technology from Adama Science and
                Technology University and software engineering certification
                from General Assembly bootcamp.
              </p>

              <p className="about-description">
                With proven experience at Washington D.C.-based fintech
                companies developing tax software solutions and data processing
                systems for the African Union's Monitoring for Environment and
                Security Unit, I consistently architect and deliver scalable
                applications that drive business outcomes across diverse
                professional environments.
              </p>

              <p className="about-description">
                My expertise spans full-stack development, system architecture,
                and performance optimization. I excel at translating complex
                business requirements into elegant technical solutions, with
                core strengths in
                <strong>strategic problem-solving</strong>,{" "}
                <strong>rapid prototyping</strong>, and{" "}
                <strong>cross-functional collaboration</strong>. Currently based
                in New York City metro area, I bring both technical depth and proven ability
                to deliver enterprise-grade solutions.
              </p>

              {/* Professional Achievements */}
              <div className="achievements-section">
                <h3 className="achievements-title">Key Achievements</h3>
                <div className="achievements-grid">
                  <div className="achievement-item">
                    <div className="achievement-icon">🚀</div>
                    <div className="achievement-content">
                      <h4>Kindred Dating Platform</h4>
                      <p>
                        Architected full-stack dating app with advanced
                        matchmaking algorithms using geospatial compatibility
                        and social graph analysis
                      </p>
                    </div>
                  </div>
                  <div className="achievement-item">
                    <div className="achievement-icon">⚡</div>
                    <div className="achievement-content">
                      <h4>Performance Leadership</h4>
                      <p>
                        Reduced API latency by 30% and increased system
                        throughput by 40% through strategic optimizations
                      </p>
                    </div>
                  </div>
                  <div className="achievement-item">
                    <div className="achievement-icon">🏛️</div>
                    <div className="achievement-content">
                      <h4>Government Systems</h4>
                      <p>
                        Led RevHub development for state-level clients,
                        improving business process efficiency by 30%
                      </p>
                    </div>
                  </div>
                  <div className="achievement-item">
                    <div className="achievement-icon">🌱</div>
                    <div className="achievement-content">
                      <h4>Sustainability Tech</h4>
                      <p>
                        Built Regen Rewards platform with JWT authentication and
                        Google OAuth, driving eco-friendly engagement
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="about-card">
              <div className="card-glow"></div>
              <div className="card-content">
                <div className="profile-section">
                  <img
                    src={profilepic}
                    alt="Dawit Endaylalu"
                    className="card-profile-image"
                  />
                  <div className="online-indicator"></div>
                </div>

                <h3 className="card-name">Dawit Endaylalu</h3>
                <p className="card-title">
                  Senior Full-Stack Engineer | MERN Stack Expert
                </p>

                <div className="card-info">
                  <div className="info-item">
                    <span className="info-label">Location</span>
                    <span className="info-value">New York City, NY/Remote</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Experience</span>
                    <span className="info-value">5+ Years Enterprise</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Current Role</span>
                    <span className="info-value status-available">
                      Lead Developer @ Validating Inc
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Specialization</span>
                    <span className="info-value">Scalable Systems & APIs</span>
                  </div>
                </div>

                <div className="social-links">
                  <a
                    href="https://www.linkedin.com/in/dawit-endaylalu/"
                    className="social-link linkedin"
                  >
                    <FontAwesomeIcon icon={faLinkedin} />
                  </a>
                  <a
                    href="https://github.com/dawit-design"
                    className="social-link github"
                  >
                    <FontAwesomeIcon icon={faGithub} />
                  </a>
                  <button
                    className="btn btn-primary"
                    onClick={() => window.open("https://drive.google.com/file/d/1FGSbRxrz-rONNhND37TJyl-xoUVJ9inq/view?usp=drive_link", "_blank")}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    <span>Download Resume</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="skills-section">
        <div className="section-container">
          <div className="section-badge">
            <span>Technical Skills</span>
          </div>

          <div className="skills-grid">
            {skills.map((skill, index) => (
              <div key={index} className="skill-item">
                <div className="skill-icon">
                  <FontAwesomeIcon icon={skill.icon} />
                </div>
                <div className="skill-info">
                  <h4 className="skill-name">{skill.name}</h4>
                  <p className="skill-desc">{skill.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
